import React, { useState, useEffect } from "react";
import PasswordInput from "../components/PasswordInput";

const PIN = "7iron";

const OPTIONS = [
  { key: "golf_dinner", label: "Golf & Dinner", color: "#166534" },
  { key: "golf_only", label: "Golf Only", color: "#1d4ed8" },
  { key: "dinner_only", label: "Dinner Only", color: "#6d28d9" },
];

export default function EventAttendance() {
  const [pinInput, setPinInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [pinError, setPinError] = useState("");
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [toast, setToast] = useState("");

  // Player Portal mode — recognises whoever's logged in from their token
  // instead of the shared PIN + "pick yourself from the list" step. Falls
  // back to the PIN flow below for anyone not logged in, or whose portal
  // account isn't linked to a Pin High roster row yet.
  const playerToken = sessionStorage.getItem("player_token");
  const [checkingPlayer, setCheckingPlayer] = useState(!!playerToken);
  const [playerMode, setPlayerMode] = useState(false);
  const [player, setPlayer] = useState(null);
  const [me, setMe] = useState(null);

  // Re-auth gate — being logged into the Player Portal generally isn't
  // enough on its own to open someone's individual vote: this confirms
  // whoever's at the keyboard right now actually knows this account's
  // password, so a device left signed in can't be used to change (or even
  // see) a different member's answer just because the portal happens to
  // still be open.
  const [needsConfirm, setNeedsConfirm] = useState(!!playerToken);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [confirming, setConfirming] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2000); };

  useEffect(() => {
    if (!playerToken) {
      const stored = sessionStorage.getItem("attendance_pin");
      if (stored === PIN) { setAuthed(true); loadData(); }
      setCheckingPlayer(false);
    }
    // If a player token exists, wait for password confirmation below
    // before fetching or revealing anything — see needsConfirm.
  }, []);

  const submitConfirm = async () => {
    setConfirmError(""); setConfirming(true);
    try {
      const res = await fetch("/api/player/verify-password", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + playerToken },
        body: JSON.stringify({ password: confirmPassword })
      });
      const d = await res.json();
      if (!res.ok) { setConfirmError(d.error || "Incorrect password"); setConfirming(false); return; }
      setNeedsConfirm(false);
      setCheckingPlayer(true);
      loadMine();
    } catch(e) { setConfirmError("Network error"); setConfirming(false); }
  };

  const loadMine = () => {
    fetch("/api/attendance/mine", { headers: { Authorization: "Bearer " + playerToken } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setEvent(d.event);
        setPlayer(d.player);
        setMe(d.me);
        setPlayerMode(true);
        // Full roster too, for the summary counts and "who else is coming"
        // list — that read stays public/no-login, same as the PIN view.
        loadData();
      })
      .catch(() => {
        // Not logged in, token expired, or account not linked yet — fall
        // back to the shared-PIN flow exactly as before.
        const stored = sessionStorage.getItem("attendance_pin");
        if (stored === PIN) { setAuthed(true); loadData(); }
      })
      .finally(() => setCheckingPlayer(false));
  };

  const loadData = () => {
    fetch("/api/attendance")
      .then(r => r.json())
      .then(d => { setEvent(d.event); setPlayers(d.players || []); })
      .catch(() => {});
  };

  const submitPin = () => {
    if (pinInput === PIN) {
      sessionStorage.setItem("attendance_pin", PIN);
      setAuthed(true);
      loadData();
    } else {
      setPinError("Incorrect PIN — try again");
    }
  };

  const saveMine = async (updates) => {
    setMe(prev => ({ ...prev, ...updates }));
    try {
      const res = await fetch("/api/attendance/mine", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + playerToken },
        body: JSON.stringify(updates)
      });
      const d = await res.json();
      if (d.error) { showToast("Error: " + d.error); loadMine(); return; }
      showToast("Saved");
      loadData(); // refresh the shared summary counts/list to match
    } catch(e) { showToast("Network error"); loadMine(); }
  };

  const pickMineAttendance = (key) => {
    saveMine({ attendance: me.attendance === key ? null : key });
  };

  const save = async (playerId, updates) => {
    setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, ...updates } : p));
    try {
      const res = await fetch("/api/attendance", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: PIN, event_id: event.id, player_id: playerId, ...updates })
      });
      const d = await res.json();
      if (d.error) { showToast("Error: " + d.error); loadData(); return; }
      showToast("Saved");
    } catch(e) { showToast("Network error"); loadData(); }
  };

  const pickAttendance = (player, key) => {
    // Clicking the already-selected option clears it; picking another
    // replaces it — the three columns are mutually exclusive per player.
    const nextAttendance = player.attendance === key ? null : key;
    save(player.id, { attendance: nextAttendance });
  };

  const toggleBuggy = (player) => {
    save(player.id, { buggy: !player.buggy });
  };

  if (playerToken && needsConfirm) {
    const playerName = sessionStorage.getItem("player_name");
    return (
      <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ background: "white", borderRadius: "16px", padding: "36px 32px", width: "100%", maxWidth: "360px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "8px" }}>🔒</div>
          <h1 style={{ fontSize: "20px", fontWeight: "800", color: "#14532d", margin: "0 0 4px" }}>Confirm it's you</h1>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: "0 0 24px" }}>Enter your password to open {playerName ? playerName.split(" ")[0] + "'s" : "your"} vote</p>
          {confirmError && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "8px 12px", borderRadius: "8px", fontSize: "13px", marginBottom: "16px" }}>{confirmError}</div>}
          <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submitConfirm()} placeholder="Your password"
            style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "8px", padding: "11px 14px", fontSize: "15px", boxSizing: "border-box" }}
            wrapperStyle={{ marginBottom: "16px" }} />
          <button onClick={submitConfirm} disabled={confirming}
            style={{ width: "100%", background: "#14532d", color: "white", border: "none", borderRadius: "8px", padding: "12px", fontSize: "15px", fontWeight: "700", cursor: confirming ? "default" : "pointer", opacity: confirming ? 0.7 : 1 }}>
            {confirming ? "Checking..." : "Confirm"}
          </button>
        </div>
      </div>
    );
  }

  if (checkingPlayer) {
    return <div style={{ minHeight: "100vh", background: "#f0fdf4" }} />;
  }

  if (!authed && !playerMode) {
    return (
      <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ background: "white", borderRadius: "16px", padding: "36px 32px", width: "100%", maxWidth: "360px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "8px" }}>⛳</div>
          <h1 style={{ fontSize: "22px", fontWeight: "800", color: "#14532d", margin: "0 0 4px" }}>Pin High</h1>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: "0 0 24px" }}>Golf & Dinner Sign-Up</p>
          {pinError && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "8px 12px", borderRadius: "8px", fontSize: "13px", marginBottom: "16px" }}>{pinError}</div>}
          <PasswordInput value={pinInput} onChange={e => setPinInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submitPin()} placeholder="Enter PIN"
            style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "8px", padding: "11px 14px", fontSize: "18px", textAlign: "center", letterSpacing: "6px", boxSizing: "border-box" }}
            wrapperStyle={{ marginBottom: "16px" }} />
          <button onClick={submitPin}
            style={{ width: "100%", background: "#14532d", color: "white", border: "none", borderRadius: "8px", padding: "12px", fontSize: "15px", fontWeight: "700", cursor: "pointer" }}>
            Enter
          </button>
          <a href="/player/login" style={{ display: "block", marginTop: "16px", fontSize: "12px", color: "#6b7280" }}>Have a Player Portal login? Sign in there instead</a>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ textAlign: "center", color: "#6b7280" }}>No upcoming event is set up yet.</div>
      </div>
    );
  }

  const counts = OPTIONS.reduce((acc, o) => ({ ...acc, [o.key]: players.filter(p => p.attendance === o.key).length }), {});
  const buggyCount = players.filter(p => p.buggy).length;
  const noResponse = players.filter(p => !p.attendance).length;

  return (
    <div style={{ minHeight: "100vh", background: "#f0fdf4" }}>
      {toast && (
        <div style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", background: "#166534", color: "white", padding: "10px 24px", borderRadius: "10px", fontSize: "14px", fontWeight: "600", zIndex: 9999, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
          {toast}
        </div>
      )}
      <div style={{ background: "#14532d", padding: "16px 20px", textAlign: "center" }}>
        <h1 style={{ color: "white", fontSize: "22px", fontWeight: "900", margin: 0 }}>⛳ {event.name || "Pin High"}</h1>
        <p style={{ color: "#86efac", fontSize: "12px", margin: "4px 0 0" }}>
          {event.date ? new Date(event.date).toLocaleDateString('en-GB') : ""}{event.venue ? ` · ${event.venue}` : ""}
        </p>
        {!playerMode && <p style={{ color: "#86efac", fontSize: "12px", margin: "4px 0 0" }}>Tick your row · tap again to clear</p>}
      </div>

      {playerMode ? (
        <div style={{ padding: "16px", maxWidth: "480px", margin: "0 auto" }}>
          <div style={{ background: "white", borderRadius: "14px", padding: "20px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "16px" }}>
            <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>Signed in as</div>
            <div style={{ fontSize: "17px", fontWeight: "800", color: "#14532d", marginBottom: "18px" }}>👤 {player.name}</div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "14px" }}>
              {OPTIONS.map(o => {
                const checked = me.attendance === o.key;
                return (
                  <button key={o.key} onClick={() => pickMineAttendance(o.key)}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px", textAlign: "left",
                      border: checked ? `2px solid ${o.color}` : "2px solid #e5e7eb",
                      background: checked ? o.color : "white", color: checked ? "white" : "#111827",
                      borderRadius: "10px", padding: "14px 16px", fontSize: "15px", fontWeight: "700", cursor: "pointer"
                    }}>
                    <span style={{ fontSize: "18px" }}>{checked ? "✅" : "☐"}</span>{o.label}
                  </button>
                );
              })}
            </div>

            <button onClick={() => saveMine({ buggy: !me.buggy })}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "10px", textAlign: "left",
                border: me.buggy ? "2px solid #ea580c" : "2px solid #e5e7eb",
                background: me.buggy ? "#ea580c" : "white", color: me.buggy ? "white" : "#111827",
                borderRadius: "10px", padding: "14px 16px", fontSize: "15px", fontWeight: "700", cursor: "pointer"
              }}>
              <span style={{ fontSize: "18px" }}>{me.buggy ? "✅" : "☐"}</span>🚗 Buggy
            </button>
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center", padding: "12px", maxWidth: "1100px", margin: "0 auto" }}>
        {OPTIONS.map(o => (
          <div key={o.key} style={{ background: o.color, color: "white", borderRadius: "20px", padding: "6px 14px", fontSize: "12px", fontWeight: "700" }}>
            {o.label}: {counts[o.key]}
          </div>
        ))}
        <div style={{ background: "#ea580c", color: "white", borderRadius: "20px", padding: "6px 14px", fontSize: "12px", fontWeight: "700" }}>Buggy: {buggyCount}</div>
        <div style={{ background: "#9ca3af", color: "white", borderRadius: "20px", padding: "6px 14px", fontSize: "12px", fontWeight: "700" }}>No response: {noResponse}</div>
      </div>

      {!playerMode && (
        <div style={{ padding: "8px 16px 24px", maxWidth: "1100px", margin: "0 auto", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
            <thead>
              <tr style={{ background: "#14532d" }}>
                <th style={thStyle}>Member</th>
                <th style={thStyle}>Handicap</th>
                {OPTIONS.map(o => <th key={o.key} style={{ ...thStyle, background: o.color }}>{o.label}</th>)}
                <th style={{ ...thStyle, background: "#ea580c" }}>Buggy</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p, i) => (
                <tr key={p.id} style={{ background: i % 2 ? "#f0fdf4" : "white" }}>
                  <td style={{ ...tdStyle, textAlign: "left", fontWeight: "700" }}>{p.name}</td>
                  <td style={tdStyle}>{p.handicap}</td>
                  {OPTIONS.map(o => (
                    <td key={o.key} style={tdStyle}>
                      <input type="checkbox" checked={p.attendance === o.key}
                        onChange={() => pickAttendance(p, o.key)}
                        style={{ width: "20px", height: "20px", cursor: "pointer", accentColor: o.color }} />
                    </td>
                  ))}
                  <td style={tdStyle}>
                    <input type="checkbox" checked={!!p.buggy}
                      onChange={() => toggleBuggy(p)}
                      style={{ width: "20px", height: "20px", cursor: "pointer", accentColor: "#ea580c" }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {playerMode && (
        <div style={{ padding: "0 16px 24px", maxWidth: "480px", margin: "0 auto" }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "14px 16px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
            <div style={{ fontSize: "12px", fontWeight: "700", color: "#6b7280", marginBottom: "10px" }}>WHO ELSE HAS RESPONDED</div>
            {players.filter(p => p.id !== player.id && p.attendance).map(p => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "5px 0", borderBottom: "1px solid #f3f4f6" }}>
                <span>{p.name}</span>
                <span style={{ color: "#166534", fontWeight: "600" }}>
                  {OPTIONS.find(o => o.key === p.attendance)?.label}{p.buggy ? " · 🚗" : ""}
                </span>
              </div>
            ))}
            {players.filter(p => p.id !== player.id && p.attendance).length === 0 && (
              <div style={{ fontSize: "13px", color: "#9ca3af" }}>No one else has responded yet.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = { color: "white", fontSize: "13px", fontWeight: "700", padding: "12px 10px", textAlign: "center" };
const tdStyle = { padding: "10px", textAlign: "center", borderTop: "1px solid #e5e7eb", fontSize: "14px" };
